// EP-161 §2.3 / S2: ที่จอดรถวันนี้. One property's visitor cars for a day,
// each with its payment state, the slip and the ML reading for reference,
// and the decisions a person takes on them (Verify, Reject, mark as fraud).
//
// Deep links (the pushes, the dashboard row) carry ?session=<id> to open a
// row, &verify=1 to open its Verify modal, and state / all_days / date as
// filters. They are read on mount (useDeepLinkParams) and then dropped from
// the URL; a push tapped while the page is open carries them again, and the
// page follows the new link (opens that row, and its Verify modal).
//
// The gate buttons (check-out, cash) of the operator app sit in each row's
// gateActions slot; the sheet and modals they open are rendered once here,
// so a check-out that moves the row out of the current tab keeps its sheet.
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Badge, Button, Card, Checkbox, DateInput, EmptyState, Input, Lightbox, Pagination, Skeleton, Tabs } from '@/components/ui'
import { VerifyPaymentModal, RejectPaymentModal } from '@/components/payments/VerificationActionModals'
import { FraudMarkModal } from '@/components/parking/sessions/FraudMarkModal'
import { GateActions, GateFlow, type GateStep } from '@/components/parking/sessions/GateActions'
import { SessionCard, SessionRow, type SessionActionHandlers } from '@/components/parking/sessions/SessionRow'
import { useAuth } from '@/hooks/useAuth'
import { useDeepLinkParams } from '@/hooks/useDeepLinkParams'
import { useLocalizationConfig } from '@/hooks/useDateFormatter'
import { useIsDesktop } from '@/hooks/useMediaQuery'
import {
  PARKING_SESSION_STATES,
  isParkingSessionState,
  parkingImageUrl,
  useInvalidateParkingSessions,
  useParkingSession,
  useParkingSessions,
} from '@/hooks/useParkingSessions'
import { localToday } from '@/lib/date'
import type { ParkingSession, ParkingSessionState } from '@/types/parking'

const DEEP_LINK_KEYS = ['session', 'verify', 'state', 'all_days', 'date'] as const
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const SEARCH_DEBOUNCE_MS = 300

type ModalState = { kind: 'verify' | 'reject' | 'fraud'; session: ParkingSession }

function parseStates(value: string | undefined): ParkingSessionState[] {
  return (value ?? '').split(',').map((s) => s.trim()).filter(isParkingSessionState)
}

function linkIdOf(link: { session?: string }): number | null {
  return Number(link.session) > 0 ? Number(link.session) : null
}

function isVerifiable(session: ParkingSession): boolean {
  return !!session.payment && (session.state === 'slip_to_verify' || session.state === 'rejected')
}

export function ParkingSessionsPage() {
  const { t, i18n } = useTranslation('parking')
  const { t: tc } = useTranslation('common')
  const { propertyId: propertyParam } = useParams<{ propertyId: string }>()
  const propertyId = Number(propertyParam)
  const { user } = useAuth()
  const canAct = user?.role === 'owner' || user?.role === 'admin' || user?.role === 'staff'
  const isDesktop = useIsDesktop()
  const { format, timezone, calendar } = useLocalizationConfig()

  const link = useDeepLinkParams(DEEP_LINK_KEYS)
  const linkedId = linkIdOf(link)
  const [states, setStates] = useState<ParkingSessionState[]>(() => parseStates(link.state))
  // null is today in the account timezone; the server resolves it.
  const [date, setDate] = useState<string | null>(() =>
    link.date && DATE_PATTERN.test(link.date) ? link.date : null,
  )
  const [allDays, setAllDays] = useState(() => link.all_days === '1' || link.all_days === 'true')
  const [search, setSearch] = useState('')
  const [q, setQ] = useState('')
  const [page, setPage] = useState(1)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set(linkedId ? [linkedId] : []))
  const [verifyFromLink, setVerifyFromLink] = useState(link.verify === '1' && linkedId != null)
  const [modal, setModal] = useState<ModalState | null>(null)
  const [slipKey, setSlipKey] = useState<string | null>(null)
  const [gateStep, setGateStep] = useState<GateStep | null>(null)

  // A new link while the page is open (a push tapped): follow it, adjusted
  // while rendering as useDeepLinkParams hands over the new values.
  const [followedLink, setFollowedLink] = useState(link)
  if (link !== followedLink) {
    setFollowedLink(link)
    const id = linkIdOf(link)
    if (id != null) setExpanded((previous) => new Set(previous).add(id))
    setVerifyFromLink(link.verify === '1' && id != null)
    setModal(null)
    if (link.state !== undefined) setStates(parseStates(link.state))
    if (link.date && DATE_PATTERN.test(link.date)) {
      setDate(link.date)
      setAllDays(false)
    }
    if (link.all_days === '1' || link.all_days === 'true') setAllDays(true)
    if (link.state !== undefined || link.date || link.all_days) setPage(1)
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setQ(search)
      setPage(1)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [search])

  const { data, isLoading, isFetching, isError, refetch } = useParkingSessions({
    propertyId,
    date,
    allDays,
    states,
    q,
    page,
  })
  const rows = useMemo(() => data?.data ?? [], [data])
  const { data: linked } = useParkingSession(linkedId)
  const invalidateSessions = useInvalidateParkingSessions()

  // A push's &verify=1 opens the Verify modal once the session has loaded,
  // only when this person may verify and the slip is still open to a decision.
  const linkedVerify: ModalState | null =
    verifyFromLink && canAct && linked && isVerifiable(linked) ? { kind: 'verify', session: linked } : null
  const activeModal = modal ?? linkedVerify
  const { data: modalDetail } = useParkingSession(
    activeModal && activeModal.kind !== 'fraud' ? activeModal.session.id : null,
  )
  const modalPayment = (modalDetail ?? activeModal?.session)?.payment
  const reference = modalPayment
    ? {
        gateEvidence: modalPayment.gate_evidence,
        fraudSuspect: modalPayment.fraud_suspect,
        reading: modalDetail?.readings_ml ?? null,
      }
    : undefined

  const closeModal = () => {
    setModal(null)
    setVerifyFromLink(false)
  }

  const toggle = (id: number) =>
    setExpanded((previous) => {
      const next = new Set(previous)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const handlers: SessionActionHandlers = {
    canAct,
    onViewSlip: setSlipKey,
    onVerify: (session) => setModal({ kind: 'verify', session }),
    onReject: (session) => setModal({ kind: 'reject', session }),
    onMarkFraud: (session) => setModal({ kind: 'fraud', session }),
  }

  const selectStates = (value: string) => {
    setStates(value === 'all' ? [] : parseStates(value))
    setPage(1)
  }
  const tabValue = states.length === 0 ? 'all' : states.join(',')
  const tabOptions = [
    { value: 'all', label: t('sessions.tabs.all') },
    ...PARKING_SESSION_STATES.map((state) => ({ value: state, label: t(`sessions.states.${state}`) })),
    // A deep link may name several states (the dashboard's unpaid row).
    ...(states.length > 1
      ? [{ value: tabValue, label: states.map((state) => t(`sessions.states.${state}`)).join(', ') }]
      : []),
  ]
  const activeFilterCount = (date || allDays ? 1 : 0) + (q ? 1 : 0)

  // The linked session may sit on another day or page; it is shown on top.
  const linkedOutside = linked && !rows.some((row) => row.id === linked.id) ? linked : null
  const RowComponent = isDesktop ? SessionRow : SessionCard
  const renderRow = (session: ParkingSession) => (
    <RowComponent
      key={session.id}
      session={session}
      expanded={expanded.has(session.id)}
      onToggle={() => toggle(session.id)}
      scrollTo={session.id === linkedId}
      gateActions={<GateActions session={session} canAct={canAct} onStep={setGateStep} />}
      {...handlers}
    />
  )
  const listed = linkedOutside ? [linkedOutside, ...rows] : rows

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: tc('nav.parking'), href: '/parking' }, { label: t('sessions.title') }]} />
      <PageHeader
        title={t('sessions.title')}
        isRefreshing={isFetching && !isLoading}
        description={
          data?.meta?.counts && (
            <span className="text-base-content/70" data-testid="parking-session-counts">
              {PARKING_SESSION_STATES.map((state) => `${t(`sessions.states.${state}`)} ${data.meta.counts[state] ?? 0}`).join(' · ')}
            </span>
          )
        }
      />

      <Card>
        <Tabs value={tabValue} onChange={selectStates} options={tabOptions} aria-label={t('sessions.tabs.aria_label')} />

        <button
          type="button"
          className="sm:hidden flex items-center justify-between w-full cursor-pointer select-none font-medium mt-3"
          aria-expanded={filtersOpen}
          aria-controls="parking-session-filters"
          onClick={() => setFiltersOpen((open) => !open)}
        >
          <span>
            {tc('filters.toggle')}
            {activeFilterCount > 0 && (
              <Badge variant="count" size="sm" className="ml-2">
                {activeFilterCount}
              </Badge>
            )}
          </span>
          <span className={`text-base-content/60 transition-transform duration-200 ${filtersOpen ? 'rotate-180' : ''}`}>▾</span>
        </button>
        <div
          id="parking-session-filters"
          className={`flex-wrap gap-4 items-end mt-3 ${filtersOpen ? 'flex' : 'hidden sm:flex'}`}
        >
          <div className="w-full sm:w-56 min-w-0">
            <DateInput
              label={t('sessions.date_label')}
              value={allDays ? '' : (date ?? localToday(timezone))}
              onChange={(e) => {
                setDate(e.target.value || null)
                setAllDays(false)
                setPage(1)
              }}
              dateFormat={format}
              timezone={timezone}
              calendar={calendar}
              locale={i18n.language}
            />
          </div>
          <Checkbox
            label={t('sessions.all_days')}
            checked={allDays}
            onChange={(e) => {
              setAllDays(e.target.checked)
              setPage(1)
            }}
          />
          <div className="w-full sm:w-56 min-w-0">
            <Input
              label={t('sessions.search_plate')}
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </Card>

      {isLoading ? (
        <Card>
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        </Card>
      ) : isError ? (
        <Card>
          <EmptyState
            title={tc('errors.something_went_wrong')}
            action={<Button onClick={() => refetch()}>{tc('actions.retry')}</Button>}
          />
        </Card>
      ) : listed.length === 0 ? (
        <Card>
          <EmptyState title={t('sessions.empty')} />
        </Card>
      ) : isDesktop ? (
        <Card noBody>
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('sessions.plate')}</th>
                  <th>{t('sessions.zone')}</th>
                  <th>{t('sessions.entered')}</th>
                  <th>{t('sessions.exited')}</th>
                  <th>{t('sessions.amount')}</th>
                  <th>{t('sessions.state')}</th>
                  <th>{t('sessions.actions')}</th>
                </tr>
              </thead>
              {listed.map(renderRow)}
            </table>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">{listed.map(renderRow)}</div>
      )}

      {data?.meta && (
        <Pagination
          currentPage={data.meta.current_page}
          totalPages={data.meta.total_pages}
          onPageChange={setPage}
          totalItems={data.meta.total_count}
          pageSize={data.meta.per_page}
        />
      )}

      {activeModal?.kind === 'verify' && activeModal.session.payment && (
        <VerifyPaymentModal
          paymentId={activeModal.session.payment.id}
          isOpen
          onClose={closeModal}
          reference={reference}
          onSuccess={invalidateSessions}
        />
      )}
      {activeModal?.kind === 'reject' && activeModal.session.payment && (
        <RejectPaymentModal
          paymentId={activeModal.session.payment.id}
          isOpen
          onClose={closeModal}
          reference={reference}
          onSuccess={invalidateSessions}
          parking
        />
      )}
      {activeModal?.kind === 'fraud' && (
        <FraudMarkModal
          sessionId={activeModal.session.id}
          licensePlate={activeModal.session.license_plate}
          isOpen
          onClose={closeModal}
        />
      )}
      <GateFlow step={gateStep} onStepChange={setGateStep} />
      {slipKey && (
        <Lightbox
          images={[slipKey]}
          index={0}
          onIndexChange={() => {}}
          onClose={() => setSlipKey(null)}
          toUrl={parkingImageUrl}
        />
      )}
    </div>
  )
}
